import {
  createMeetingsContext,
  isMeetingSessionRoleGreaterThanOrEqual,
  MeetingContextData,
  MeetingOrderedSession,
  MeetingSessionOvenMediaEmitterManager,
  MeetingSessionOvenMediaReceiverManager,
  MeetingSessionOvenMediaReceiverManagerBaseData,
  MeetingSource,
  MeetingSourceDeviceType,
  useMeetingContextDataOnChange,
  useMeetingContextDataSelector,
  useMultiMeetingContextDataSelector,
} from "@kalyzee/kast-web-sdk";
import { MeetingContainer, MeetingContainerAPI } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import { Store } from "@kalyzee/kast-web-sdk/helpers/state/store";
import { destroyMediaStream, MediaStreamEmptyVideoTrack, MediaStreamSilentTrack, useRefAutoUpdate, useStateWithRef } from "@kalyzee/kast-web-sdk/web-components";
import {
  OvenMediaEmitterWebRTCSessionOptions,
  OvenMediaReceiverWebRTCSession,
  OvenMediaReceiverWebRTCSessionOptions,
  RTCStreamParams,
  WebRTCSessionState,
} from "@kalyzee/kast-web-sdk/webrtc";
import {
  Meeting,
  MeetingPermissions,
  MeetingRoom,
  MeetingRoomStatus,
  MeetingRoomType,
  MeetingSession,
  MeetingSessionAlertState,
  MeetingSessionMedia,
  MeetingSessionStatus,
} from "@kalyzee/kast-web-sdk/websocket";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getAudioContextForVolumeAnalyzer } from "../helpers/audio";
import { OvenMediaSessionService } from "../helpers/ovenmedia";
import { sdkClient } from "../helpers/sdk";
import { toastError } from "../helpers/toast";
import { PopulatedMeetingSession } from "../interfaces/populatedData";
import { RequiredSome } from "../interfaces/utils";
import { useSettings } from "./settings";
import { MeetingSessionRole } from "@kalyzee/kast-web-sdk/http";

export interface MeetingSourceControls {
  type: "aver-ptz";
  hostname?: string;
  username?: string;
  password?: string;
  [key: string]: any;
}

export type MeetingSourceExtended = RequiredSome<MeetingSource<any, { type: "camera" | "screen" }>, "extra"> & {
  controls?: MeetingSourceControls;
  visible?: boolean;
};

const DEFAULT_MEETING_SOURCES: MeetingSourceExtended[] = [
  {
    name: "webcam",
    devices: [
      {
        mode: "audiovideo",
        type: MeetingSourceDeviceType.WEB,
        data: {
          mediaConstraints: {
            video: true,
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
          },
        },
      },
    ],
    extra: {
      type: "camera",
    },
    visible: true,
  },
];

export const useMeetingSources = (): MeetingSourceExtended[] => {
  const [settings] = useSettings();
  const sources = (settings.sources ?? DEFAULT_MEETING_SOURCES).filter((s) => s.devices?.length);
  sources.forEach((s) => {
    if (!s.extra) s.extra = { type: "camera" };
    else if (!s.extra.type) s.extra.type = "camera";
  });
  return sources.filter((s) => !s.disabled) as MeetingSourceExtended[];
};

export const useMeetingVideoFeedbackSource = (): MeetingSource | undefined => {
  const [settings] = useSettings();
  return settings?.videoFeedback?.source;
};

export const useMeetingWebcamFeedbackSource = (): MeetingSourceExtended => {
  const [settings] = useSettings();
  const source = settings?.webcamFeedback?.source ?? DEFAULT_MEETING_SOURCES[0];
  return source as MeetingSourceExtended;
};

export interface MeetingContextOptions {
  ovenMediaSession?: {
    emission?: {
      options?: MeetingSessionOvenMediaEmitterManager["ovenMediaSessionOptionsExtractor"];
    };
    reception?: {
      options?: MeetingSessionOvenMediaReceiverManager["ovenMediaSessionOptionsExtractor"];
    };
  };
}

export const meetingsContext = createMeetingsContext();

export const useMeetingContext = () => {
  return useContext(meetingsContext);
};

export const useMeetingContainer = (meetingId?: string): MeetingContainer | undefined => {
  const context = useMeetingContext();
  const containers = context?.api?.getContainers();
  const container = useMemo(() => {
    return containers?.find((c) => c.getMeetingId() === meetingId);
  }, [containers, meetingId]);
  return container;
};

export const useMeetingContainerAPI = (meetingId?: string): MeetingContainerAPI | undefined => {
  const meetingContainer = useMeetingContainer(meetingId);
  const meetingAPI = useMemo(() => {
    return meetingContainer?.getAPI();
  }, [meetingContainer]);
  return meetingAPI;
};

/**
 * This hooks switches the current selected meeting container
 * to the pedagogy one when it's available and fullfils the conditions.
 * Otherwise, the first available container is selected.
 */
export const useMultiMeetingAPI = (selectedId?: string, roomEnlistmentRequired = false, onlyFocusedCanBeRunning = false) => {
  const context = useMeetingContext();
  const RTC_STATS_INTERVAL = 1000; // < 0 to disable it

  const containers = context?.api?.getContainers();
  const containersRef = useRefAutoUpdate(containers);

  (window as any).containers = containers;

  // The online pedagogy meeting i am connected to
  // If i am connected to multiple pedagogy meetings,
  // priority to the one that has a room waiting for me
  const getPeda = useCallback(() => {
    if (!containersRef.current) return undefined;

    let candidate: MeetingContainer | undefined;
    let candidateRoom: MeetingRoom | undefined;

    for (const container of containersRef.current) {
      const state = container.getStore().getState();

      const userId = state.session?.user?.id;
      if (!userId) continue;

      const isPeda = state.meeting?.specializedData?.isPedagogy;
      if (!isPeda) continue;

      candidate = container;

      const room = state.rooms?.find((r) => r.type === MeetingRoomType.Group && r.status !== MeetingRoomStatus.Closed && r.enlistedUserIds?.includes(userId));
      if (room) {
        candidateRoom = room;
        break;
      }
    }

    if (!candidate) return undefined;

    // A peda meeting was found, maybe it also has a room waiting for me
    return { container: candidate, myRoom: candidateRoom };
  }, []);

  // The meetings i am running in (my session is running)
  const getRunning = useCallback(() => {
    return (
      containersRef.current?.filter((c) => {
        const state = c.getStore().getState();
        const session = state.session;
        if (!session) return false;
        return session.status === MeetingSessionStatus.RUNNING;
      }) ?? []
    );
  }, []);

  const [peda, setPeda] = useState(getPeda());
  const [running, setRunning] = useState(getRunning());

  const selected = useMemo(() => {
    if (!selectedId) return containers?.[0];
    return containers?.find((c) => c.getMeetingId() === selectedId);
  }, [containers, selectedId]);

  const focused = useMemo(() => {
    let focusCandidate: MeetingContainer | undefined;

    if (peda && (peda.myRoom || !roomEnlistmentRequired)) {
      focusCandidate = peda.container;
    } else {
      focusCandidate = selected;
    }

    if (!focusCandidate) return undefined;
    if (!onlyFocusedCanBeRunning) return focusCandidate;

    // We check if an other meeting has a running session
    const anyOtherRunning = running?.some((r) => r.getMeetingId() !== focusCandidate?.getMeetingId());
    // If yes, we can't join the candidate yet
    if (anyOtherRunning) return undefined;

    return focusCandidate;
  }, [peda, selected, running, roomEnlistmentRequired, onlyFocusedCanBeRunning]);

  useEffect(() => {
    setRunning(getRunning());
    setPeda(getPeda());

    // we listen to an eventual pedagogy container,
    // we also stay aware of when the pedagogy container is removed
    const handles: ReturnType<Store<any>["registerSelector"]>[] = [];

    // staying aware of interesting changes in every containers
    containers?.forEach((c) => {
      // those changes may effect the pedagogy meeting
      handles.push(
        c.getStore().registerSelector(
          (state) => state.meeting?.specializedData?.isPedagogy,
          () => {
            setPeda(getPeda());
          }
        )
      );
      handles.push(
        c.getStore().registerSelector(
          (state) => state.rooms,
          () => {
            setPeda(getPeda());
          }
        )
      );
      // those changes may effect the running meetings
      handles.push(
        c.getStore().registerSelector(
          (state) => state.session?.status,
          () => {
            setRunning(getRunning());
          }
        )
      );
    });

    return () => handles?.forEach((h) => h.flush());
  }, [containers]);

  const api = useMemo(() => {
    return {
      all: context?.api,
      selectedMeetingAPI: selected?.getAPI(),
      pedagogy: peda,
      focusedMeetingAPI: focused?.getAPI(),
    };
  }, [selected, focused, peda, context]);

  return api;
};

export const useFocusedMeetingAPI = () => {
  const api = useMultiMeetingAPI();
  return api.focusedMeetingAPI;
};

export const useMeetingConfigurator = (meetingContainer: MeetingContainerAPI | MeetingContainerAPI[] | undefined, options: MeetingContextOptions) => {
  const [settings] = useSettings();
  useEffect(() => {
    if (meetingContainer === undefined) return;
    const containers = Array.isArray(meetingContainer) ? meetingContainer : [meetingContainer];
    containers.forEach((c) => {
      c.emissionManager.ovenMediaSessionOptionsExtractor = (mediaSession, mediaStream) => {
        const result: OvenMediaEmitterWebRTCSessionOptions = {
          waitingForSessionReadyTimeout: 10000,
        };
        const curr = options?.ovenMediaSession?.emission?.options?.(mediaSession, mediaStream);
        if (curr) Object.assign(result, curr);
        return result;
      };
      c.receptionManager.ovenMediaSessionOptionsExtractor = (session: MeetingSession, mediaSession: MeetingSessionMedia) => {
        const result: OvenMediaReceiverWebRTCSessionOptions = {};
        const curr = options?.ovenMediaSession?.reception?.options?.(session, mediaSession);
        if (curr) Object.assign(result, curr);
        return result;
      };
    });
  }, [settings, meetingContainer, options]);
};

export const useAutoOnOffVideos = (meetingContainer: MeetingContainerAPI | undefined, visibleSessions: PopulatedMeetingSession[]) => {
  const [initState, setInitState] = useState<{ [id: string]: boolean }>({});
  const [receptionSessions] = useMeetingSelector(meetingContainer, (state) => state.receptionSessions);

  const updateVideo = useCallback(
    (session: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>, force?: boolean) => {
      const isInit = initState[session.data.mediaSession.id];
      if (!isInit && !force) return; // was not set in a playlist event yet

      const canBeSeen = visibleSessions.some((s) => s.id === session.data.session.id);

      if (canBeSeen) {
        if (!OvenMediaSessionService.videoIsEnabled(session)) OvenMediaSessionService.enableVideo(session);
      } else {
        if (OvenMediaSessionService.videoIsEnabled(session)) OvenMediaSessionService.disableVideo(session);
      }

      if (!isInit) setInitState((state) => ({ ...state, [session.data.mediaSession.id]: true }));
    },
    [initState, visibleSessions]
  );

  useEffect(() => {
    if (!receptionSessions?.length) return;
    const listeners: {
      session: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>;
      state: (state: WebRTCSessionState) => void;
    }[] = [];
    receptionSessions.forEach((s) => {
      const listener = {
        session: s,
        state: (state: WebRTCSessionState) => {
          if (state === WebRTCSessionState.RUNNING) updateVideo(s, true); // true so the state entry is created
        },
      };
      listeners.push(listener);
      s.addEventListener("state", listener.state);
    });
    receptionSessions.forEach((s) => updateVideo(s)); // will only apply to ready sessions
    return () => {
      listeners.forEach((l) => {
        l.session.removeEventListener("state", l.state);
      });
    };
  }, [meetingContainer, updateVideo]);
};

export const useMeetingDataOnChange = <T>(
  state: MeetingContainerAPI | undefined,
  selector: (state: MeetingContextData) => T,
  callback: (value: T, previous: T) => void
) => {
  useMeetingContextDataOnChange(state, selector, callback);
};

export const useMeetingSelector = <T>(state: MeetingContainerAPI | undefined, selector: (state: MeetingContextData) => T) => {
  const value = useMeetingContextDataSelector(state, selector);
  const refValue = useRefAutoUpdate(value);
  return [value, refValue] as const;
};

export const useMultiMeetingSelector = <T>(state: MeetingContainerAPI[], selector: (state: MeetingContextData) => T) => {
  const value = useMultiMeetingContextDataSelector(state, selector);
  const refValue = useRefAutoUpdate(value);
  return [value, refValue] as const;
};

interface StreamData {
  stream: MediaStream;
  ready: boolean;
}

export const useDynamicMedia = (
  meetingContainer: MeetingContainerAPI | undefined,
  callbacks?: {
    onStreamStart?: (name: string, success: boolean) => void;
  }
) => {
  const session = useMeetingContextDataSelector(meetingContainer, (state) => state.session);

  const streamDataRef = useRef<Record<string, StreamData>>({});
  const [, setForceUpdate] = useState({});

  // there was issues when storing streams in a state, the "set" function is being called too quickly
  // problem solved with a ref but we still need to force renders for the get() function
  const update = () => setForceUpdate({});

  const startStreamOnMedia = async (name: string, media: MeetingSessionMedia, streamData: StreamData, params?: RTCStreamParams): Promise<boolean> => {
    if (!meetingContainer) return false;
    streamData.ready = false;
    const success = await meetingContainer.setMediaStream(media.id, streamData.stream, params);
    streamData.ready = true;
    if (!success) return false;
    callbacks?.onStreamStart?.(name, !!success);
    return true;
  };

  useEffect(() => {
    if (!meetingContainer) return;
    const ovenMediaSessionListener = meetingContainer.emissionManager.addEventListener("create", (s) => {
      const mediaName = s.data.mediaSession.name;
      if (!mediaName) return;

      const streamData = streamDataRef.current[mediaName];
      if (!streamData) return;

      startStreamOnMedia(mediaName, s.data.mediaSession, streamData);
    });
    return () => {
      ovenMediaSessionListener?.flush();
    };
  }, [meetingContainer, callbacks]);

  const set = async (
    name: string,
    stream: MediaStream,
    additionalData?: { extraType?: string; deviceId?: string },
    params?: RTCStreamParams
  ): Promise<boolean> => {
    if (!meetingContainer) return false;
    if (!session) return false;

    const currentStreamData = streamDataRef.current[name];

    if (currentStreamData) {
      if (!currentStreamData.ready) return false;
      const currentMedia = session.medias.find((m) => m.name === name);
      if (currentMedia) {
        // stream has been set and a media exists remotely
        currentStreamData.stream = stream;
        update();
        return startStreamOnMedia(name, currentMedia, currentStreamData, params);
      }
      return false; // a stream with this name is already waiting for a media
    }

    streamDataRef.current[name] = { stream, ready: false };
    update();

    const { deviceId, extraType } = additionalData ?? {};
    const group = extraType === "camera" ? "cameras" : extraType === "screen" ? "screens" : "undefined";
    const result = await meetingContainer.updater.setMedias(session.id, [
      ...session.medias,
      {
        video: {
          deviceType: "camera",
          enabled: meetingContainer.emissionManager.videoIsEnabled(group),
        },
        audio: {
          deviceType: "audio",
          enabled: meetingContainer.emissionManager.audioIsEnabled(group),
        },
        name: name,
        deviceId,
        extra: extraType
          ? {
              type: extraType,
            }
          : undefined,
      },
    ]);
    if (result?.error || !result?.response) {
      console.error(`Could not set media "${name}" : `, result?.error);
      remove(name);
      return false;
    }

    streamDataRef.current[name].ready = true;

    return true;
  };

  // TODO : add a way to update the stream
  const get = (name: string) => streamDataRef.current[name]?.stream;

  const remove = async (name: string): Promise<boolean> => {
    if (!meetingContainer) return false;
    if (!session) return false;
    if (!streamDataRef.current[name]) return false;

    delete streamDataRef.current[name];
    update();

    // TODO : what if this fails, we need to mark it as needing to be removed
    const result = await meetingContainer.updater.setMedias(
      session.id,
      session.medias.filter((m) => m.name !== name)
    );
    if (result?.error || !result?.response) {
      console.error(`Could not remove media "${name}" : `, result?.error);
      return false;
    }

    return true;
  };

  return {
    set,
    get,
    remove,
  };
};

export const useHasOngoingGroups = (context: MeetingContainerAPI | undefined): boolean => {
  const rooms = useMeetingContextDataSelector(context, (state) => state.rooms);
  return useMemo(() => {
    const match = rooms?.some((r) => r.type === MeetingRoomType.Group && r.status === MeetingRoomStatus.Ongoing);
    return !!match;
  }, [rooms]);
};

export const RestrictedAudioPermission = "[[role;>=;moderator]||[highlighting;==;ongoing]]";

export const usePermission = (meeting: Meeting | undefined) =>
  useMemo(() => {
    const hasPermission = (category: keyof MeetingPermissions, permission: string) => {
      return meeting?.permissions?.[category]?.includes(permission);
    };

    const setPermission = async (category: keyof MeetingPermissions, permission: string, enabled: boolean) => {
      const meetingId = meeting?.id;
      if (!meetingId) return;

      const newPermissions = { ...(meeting.permissions ?? {}) };
      const categoryPermissions = new Set<string>(meeting.permissions?.[category] ?? []);

      if (enabled) categoryPermissions.add(permission);
      else categoryPermissions.delete(permission);
      newPermissions[category] = Array.from(categoryPermissions);

      const websocket = sdkClient.getWebsocketClient();
      const result = await websocket.emitter.meeting.updateById({ id: meetingId, permissions: newPermissions });
      if (!result.response || result.error) {
        toastError("Une erreur est survenue...");
        return;
      }
    };

    const addPermission = async (category: keyof MeetingPermissions, permission: string) => setPermission(category, permission, true);

    const removePermission = async (category: keyof MeetingPermissions, permission: string) => setPermission(category, permission, false);

    return { hasPermission, setPermission, addPermission, removePermission };
  }, [meeting]);

/**
 * - the webcam management API is handled on the expert meeting level, wrapping all
 * the possible expert pages
 * - if we pass from one view requiring the expert webcam to an other one requiring it too,
 * we now longer have to delete and set the expert_webcam, which leads to errors
 */

export const useDynamicWebcam = (meetingContainerAPI: MeetingContainerAPI | undefined, name: string, stream?: MediaStream) => {
  const dynamicMediaAPI = useDynamicMedia(meetingContainerAPI);

  useEffect(() => {
    if (stream) {
      dynamicMediaAPI.set(name, stream, { extraType: "camera" });
    } else {
      dynamicMediaAPI.remove(name);
    }
  }, [dynamicMediaAPI, stream]);

  useEffect(() => {
    return () => {
      dynamicMediaAPI.remove(name);
    };
  }, []);
};

export const useEmptyMediaStream = (enabled: boolean = false): MediaStream | undefined => {
  const videoTrackRef = useRef<MediaStreamEmptyVideoTrack>();
  const audioTrackRef = useRef<MediaStreamSilentTrack>();
  const [mediaStream, setMediaStream, mediaStreamRef] = useStateWithRef<MediaStream>();

  const generateVideoTrack = () => {
    let result: MediaStreamEmptyVideoTrack | undefined = undefined;
    try {
      result = new MediaStreamEmptyVideoTrack();
    } catch (err) {}
    return result;
  };

  const generateAudioTrack = () => {
    let result: MediaStreamSilentTrack | undefined = undefined;
    try {
      result = new MediaStreamSilentTrack(getAudioContextForVolumeAnalyzer());
    } catch (err) {}
    return result;
  };

  const generateMediaStream = () => {
    if (!videoTrackRef.current) videoTrackRef.current = generateVideoTrack();
    if (!audioTrackRef.current) audioTrackRef.current = generateAudioTrack();

    const videoTrack = videoTrackRef.current?.track;
    const audioTrack = audioTrackRef.current?.track;
    if (!videoTrack || !audioTrack) return undefined;
    return new MediaStream([videoTrack, audioTrack]);
  };

  const clean = () => {
    videoTrackRef.current?.destroy();
    audioTrackRef.current?.destroy();
    if (mediaStreamRef.current) destroyMediaStream(mediaStreamRef.current);
  };

  useEffect(() => {
    if (enabled) {
      setMediaStream(generateMediaStream())
    }
    return () => clean();
  }, [enabled]);

  return mediaStream;
};
